<template>
    <div>
        <title-bar :title-stack="titleStack" />
        <hero-bar>
            Exporturi ({{ exports.length }})

            <div slot="right">
                <template v-if="isAtLeastOneExportGenerating">
                    <b-tooltip
                        label="Poti genera un nou export dupa finalizarea celor in procesare"
                        type="is-dark"
                        position="is-left"
                    >
                        <b-button
                            :disabled="isAtLeastOneExportGenerating"
                            type="is-info"
                            icon-left="database-export"
                            class="add-account-button"
                            @click="isCreateModalActive = true"
                        >
                            Export nou
                        </b-button>
                    </b-tooltip>
                </template>
                <template v-else>
                    <b-button
                        :disabled="isAtLeastOneExportGenerating"
                        type="is-info"
                        icon-left="database-export"
                        class="add-account-button"
                        @click="isCreateModalActive = true"
                    >
                        Export nou
                    </b-button>
                </template>
            </div>
        </hero-bar>
        <b-modal
            v-model="isCreateModalActive"
            has-modal-card
            trap-focus
            :destroy-on-hide="true"
            aria-role="dialog"
            aria-modal
        >
            <template>
                <export-modal @close="closeModal" @saved="closeModal"></export-modal>
            </template>
        </b-modal>
        <section class="section is-main-section custom-section">
            <card-component
                :title="`${exportedForms} formulare exportate`"
                class="has-table has-mobile-sort-spaced"
                icon="format-align-justify"
            >
                <b-table
                    :debounce-search="500"
                    :checkable="false"
                    :paginated="true"
                    :per-page="perPage"
                    :striped="true"
                    :hoverable="true"
                    :loading="isLoading"
                    default-sort="name"
                    :data="exports"
                >
                    <b-table-column centered label="ID" field="id" sortable v-slot="props">
                        {{ props.row.id }}
                    </b-table-column>
                    <b-table-column centered label="Formulare exportate" field="formsCount" sortable v-slot="props">
                        {{ props.row.formsCount }}
                    </b-table-column>
                    <b-table-column centered label="An" field="year" sortable v-slot="props">
                        {{ props.row.year }}
                    </b-table-column>
                    <b-table-column centered label="Status" field="status" sortable v-slot="props">
                        <b-tag :type="props.row.status == 0 ? 'is-info' : 'is-primary'">{{
                            props.row.status == 1 ? 'Finalizat' : 'Se genereaza'
                        }}</b-tag>
                    </b-table-column>
                    <b-table-column centered label="Data" field="createdAt" v-slot="props">
                        {{ moment(new Date(props.row.createdAt)).format('DD-MM-YYYY HH:mm') }}
                    </b-table-column>
                    <b-table-column custom-key="actions" cell-class="is-actions-cell" v-slot="props">
                        <div class="buttons is-right">
                            <b-tooltip
                                :label="props.row.status == 0 ? 'Se genereaza exportul' : 'Descarca export'"
                                type="is-dark"
                                position="is-left"
                            >
                                <b-button
                                    :loading="props.row.status == 0"
                                    size="is-small"
                                    type="is-primary"
                                    @click="downloadExport(props.row)"
                                >
                                    <b-icon icon="download" size="is-small" />
                                </b-button>
                            </b-tooltip>
                            <button
                                :loading="isLoading"
                                :disabled="props.row.status == 0"
                                class="button is-small is-danger"
                                type="button"
                                @click="deleteExport(props.row.id)"
                            >
                                <b-icon icon="trash-can" size="is-small" />
                            </button>
                        </div>
                    </b-table-column>

                    <section slot="empty" class="section">
                        <div class="content has-text-grey has-text-centered">
                            <template v-if="isLoading">
                                <p>
                                    <b-icon icon="dots-horizontal" size="is-large" />
                                </p>
                                <p>Se incarca...</p>
                            </template>
                            <template v-else>
                                <p>
                                    <b-icon icon="emoticon-sad" size="is-large" />
                                </p>
                                <p>Nu exista date&hellip;</p>
                            </template>
                        </div>
                    </section>
                </b-table>
            </card-component>
        </section>
    </div>
</template>

<script>
import CardComponent from '@/components/CardComponent';
import TitleBar from '@/components/TitleBar';
import HeroBar from '@/components/HeroBar';
import ExportModal from './components/ExportModal';
import { mapState } from 'vuex';
import moment from 'moment';
import * as toast from '@/utils/toast';
import * as dialogs from '@/utils/dialog';

export default {
    name: 'Conturi',
    components: {
        CardComponent,
        HeroBar,
        TitleBar,
        ExportModal,
    },
    data: () => ({
        page: 1,
        isLoading: false,
        perPage: 50,
        isCreateModalActive: false,
        moment,
        exportsCheckInterval: null,
    }),
    computed: {
        ...mapState({
            exports: (state) => state.exports.list,
        }),
        titleStack() {
            return ['Admin', 'Declaratie 230', 'Exporturi'];
        },
        exportedForms() {
            return this.exports.reduce((cum, item) => {
                if (item == null) return cum;
                cum += item.formsCount;
                return cum;
            }, 0);
        },
        isAtLeastOneExportGenerating() {
            return this.exports.some((item) => item && item.status == 0);
        },
    },
    methods: {
        async getExports(withoutLoading = false) {
            if (!withoutLoading) this.isLoading = true;
            try {
                await this.$store.dispatch('exports/GetExports');
            } catch (err) {
                this.isLoading = false;
            } finally {
                this.isLoading = false;
            }
        },
        closeModal() {
            this.isCreateModalActive = false;
            return this.getExports();
        },
        async deleteExport(id) {
            const { result } = await dialogs.confirm(
                'Atentie',
                `Sterg exportul ${id}? Toate formularele asociate fi scoase din export.`,
                'Confirm',
                'is-warning'
            );
            if (!result) return false;
            this.isLoading = true;
            try {
                await this.$store.dispatch('exports/RemoveExport', id);
            } catch (err) {
                return false;
            } finally {
                this.isLoading = false;
            }
        },
        downloadExport(exportObject) {
            const downloadLink = `${process.env.VUE_APP_API_URL}/downloads/exports/${exportObject.path}`;
            window.open(downloadLink, '_blank').focus();
        },
    },
    async created() {
        await this.getExports();
        this.exportsCheckInterval = setInterval(this.getExports.bind(this, true), 10000);
    },
    beforeDestroy() {
        clearInterval(this.exportsCheckInterval);
    },
};
</script>

<style lang="scss" scoped>
::v-deep .level {
    align-items: baseline;
}

.is-fullwidth {
    width: 100%;
}

.add-account-button {
    margin-left: 10px;
}
</style>
